/**
 * geoUtils - Utilidades geográficas compartidas (web + mobile)
 *
 * Operaciones GeoJSON sin dependencias: punto-en-polígono (ray casting),
 * área esférica de un polígono en hectáreas, distancia Haversine,
 * centroide y validación de geometrías.
 *
 * Las coordenadas siguen el orden GeoJSON: [longitud, latitud].
 */

export type Coordenada = [number, number]; // [lng, lat]

export type GeometriaGeoJSON =
  | { type: 'Point'; coordinates: Coordenada }
  | { type: 'LineString'; coordinates: Coordenada[] }
  | { type: 'Polygon'; coordinates: Coordenada[][] };

/** Radio medio de la Tierra en metros (WGS-84). */
const RADIO_TIERRA_M = 6378137;

/**
 * Límites anti-abuso del Mapa del Campo. Un dibujo gigante (mapa muy
 * alejado, o un actor malicioso escribiendo directo a Supabase) puede
 * cubrir una provincia entera y matar el servidor: el bake NDVI genera
 * grillas/tiles proporcionales al bbox dibujado (verificado: un clic a
 * zoom país ≈ 104M ha).
 *
 * El límite de la empresa es de COBERTURA (área del bbox que combina TODOS
 * los elementos: lo que el bake hornea), no de suma de áreas — dos cuadros
 * chicos en esquinas opuestas de una provincia suman poco pero su bbox la
 * cubre entera.
 *
 * ⚠️ Valores ESPEJADOS en las migraciones SQL
 * (20260902120000/20260902130000_limites_hectareas_elementos.sql):
 * constraint por elemento + trigger de cobertura por empresa. Si cambiás
 * acá, cambiá allá (y viceversa).
 */
export const MAX_HECTAREAS_ELEMENTO = 200_000; // bbox de un solo elemento (~45×45 km)
export const MAX_COBERTURA_TILES_EMPRESA = 200_000; // bbox combinado de toda la empresa

/**
 * Área (ha) del bbox que envuelve TODAS las geometrías: la cobertura de
 * tiles que el bake NDVI procesaría. Espeja el trigger SQL
 * valida_cobertura_tiles_empresa() para poder advertir ANTES de guardar.
 */
export function coberturaHectareas(geos: (GeometriaGeoJSON | null | undefined)[]): number {
  let minlng = Infinity;
  let maxlng = -Infinity;
  let minlat = Infinity;
  let maxlat = -Infinity;
  const sumar = (c: Coordenada) => {
    if (!c || !Number.isFinite(c[0]) || !Number.isFinite(c[1])) return;
    minlng = Math.min(minlng, c[0]);
    maxlng = Math.max(maxlng, c[0]);
    minlat = Math.min(minlat, c[1]);
    maxlat = Math.max(maxlat, c[1]);
  };
  for (const g of geos) {
    if (!g?.coordinates) continue;
    if (g.type === 'Point') sumar(g.coordinates);
    else (g.type === 'Polygon' ? g.coordinates[0] || [] : g.coordinates).forEach(sumar);
  }
  if (!Number.isFinite(minlng)) return 0;
  return (
    (maxlng - minlng) * 111320 * Math.cos((Math.PI / 180) * ((minlat + maxlat) / 2))
    * (maxlat - minlat) * 110540
  ) / 10000; // m² → ha
}

/**
 * ¿El punto (lng, lat) está dentro del polígono?
 * Ray casting (implementación del algoritmo even-odd).
 * El anillo exterior del polígono puede o no estar cerrado.
 */
export function puntoEnPoligono(lng: number, lat: number, poligono: Coordenada[]): boolean {
  let dentro = false;
  const n = poligono.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = poligono[i];
    const [xj, yj] = poligono[j];
    const intersecta =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi + Number.EPSILON) + xi;
    if (intersecta) dentro = !dentro;
  }
  return dentro;
}

/**
 * Busca en qué elemento (de una lista) cae el punto. Devuelve el primer
 * match (los polígonos no deberían solaparse en la práctica).
 */
export function elementoEnPunto<T extends { geometria: GeometriaGeoJSON }>(
  lng: number,
  lat: number,
  elementos: T[]
): T | null {
  for (const el of elementos) {
    if (el.geometria?.type === 'Polygon') {
      if (puntoEnPoligono(lng, lat, el.geometria.coordinates[0] || [])) return el;
    }
  }
  return null;
}

/**
 * Un punto GARANTIZADO dentro del polígono, lo más cercano posible a
 * `preferido` (o al centro del bbox si no viene). `centroide` puede caer
 * afuera en formas cóncavas (L, C) — este fallback barre una grilla de
 * 25×25 sobre el bbox y elige el punto interior más cercano al preferido.
 * ~625 evaluaciones por polígono: barato y pensado para memoizarlo.
 */
export function puntoInterior(
  geo: GeometriaGeoJSON,
  preferido?: Coordenada | null
): Coordenada | null {
  if (geo?.type !== 'Polygon') return null;
  const anillo = geo.coordinates[0] || [];
  if (anillo.length < 3) return null;
  if (preferido && puntoEnPoligono(preferido[0], preferido[1], anillo)) return preferido;

  let minlng = Infinity, maxlng = -Infinity, minlat = Infinity, maxlat = -Infinity;
  for (const [lng, lat] of anillo) {
    if (lng < minlng) minlng = lng;
    if (lng > maxlng) maxlng = lng;
    if (lat < minlat) minlat = lat;
    if (lat > maxlat) maxlat = lat;
  }
  const cx = preferido ? preferido[0] : (minlng + maxlng) / 2;
  const cy = preferido ? preferido[1] : (minlat + maxlat) / 2;

  let mejor: Coordenada | null = null;
  let mejorDist = Infinity;
  const N = 24;
  for (let i = 0; i <= N; i++) {
    for (let j = 0; j <= N; j++) {
      const lng = minlng + ((maxlng - minlng) * i) / N;
      const lat = minlat + ((maxlat - minlat) * j) / N;
      if (!puntoEnPoligono(lng, lat, anillo)) continue;
      const d = (lng - cx) ** 2 + (lat - cy) ** 2;
      if (d < mejorDist) {
        mejorDist = d;
        mejor = [lng, lat];
      }
    }
  }
  return mejor;
}

/**
 * Ancho HORIZONTAL del polígono (en grados de longitud) a la altura `lat`,
 * del segmento even-odd que contiene a `lngDentro`. Es lo que realmente
 * disponible para apoyar una FILA de íconos: el radio al vértice más lejano
 * engaña en formas alargadas o en L (un brazo angosto tiene vértices
 * lejanos pero poca cuerda). 0 si el punto no cae en ningún segmento.
 */
export function anchoEnLatitud(
  anillo: Coordenada[],
  lat: number,
  lngDentro: number
): number {
  const cruces: number[] = [];
  for (let i = 0, j = anillo.length - 1; i < anillo.length; j = i++) {
    const [xi, yi] = anillo[i];
    const [xj, yj] = anillo[j];
    if ((yi > lat) !== (yj > lat)) {
      cruces.push(((xj - xi) * (lat - yi)) / (yj - yi + Number.EPSILON) + xi);
    }
  }
  cruces.sort((a, b) => a - b);
  for (let k = 0; k + 1 < cruces.length; k += 2) {
    if (lngDentro >= cruces[k] && lngDentro <= cruces[k + 1]) {
      return cruces[k + 1] - cruces[k];
    }
  }
  return 0;
}

/**
 * Alto VERTICAL del polígono (en grados de latitud) sobre la longitud `lng`,
 * del segmento even-odd que contiene a `latDentro`. La contraparte de
 * `anchoEnLatitud`: juntas definen la caja disponible para apoyar la GRILLA
 * de íconos con la proporción del cuadro. 0 si el punto no cae en ningún
 * segmento.
 */
export function altoEnLongitud(
  anillo: Coordenada[],
  lng: number,
  latDentro: number
): number {
  const cruces: number[] = [];
  for (let i = 0, j = anillo.length - 1; i < anillo.length; j = i++) {
    const [xi, yi] = anillo[i];
    const [xj, yj] = anillo[j];
    if ((xi > lng) !== (xj > lng)) {
      cruces.push(((yj - yi) * (lng - xi)) / (xj - xi + Number.EPSILON) + yi);
    }
  }
  cruces.sort((a, b) => a - b);
  for (let k = 0; k + 1 < cruces.length; k += 2) {
    if (latDentro >= cruces[k] && latDentro <= cruces[k + 1]) {
      return cruces[k + 1] - cruces[k];
    }
  }
  return 0;
}

/**
 * Área de un polígono (anillo exterior) en HECTÁREAS, sobre la esfera
 * (fórmula de exceso esférico, la misma que usa geojson-area / turf).
 */
export function areaHectareas(poligono: Coordenada[]): number {
  if (poligono.length < 3) return 0;

  // Normaliza cierre: el primer punto repetido al final no suma.
  const anillo = cerrarAnillo(poligono);
  const n = anillo.length;
  if (n < 4) return 0;

  let total = 0;
  const rad = Math.PI / 180;
  for (let i = 0; i < n - 1; i++) {
    const [x1, y1] = anillo[i];
    const [x2, y2] = anillo[i + 1];
    total += (x2 - x1) * rad * (2 + Math.sin(y1 * rad) + Math.sin(y2 * rad));
  }
  const areaM2 = Math.abs((total * RADIO_TIERRA_M * RADIO_TIERRA_M) / 2);
  return areaM2 / 10000; // m² → ha
}

/** Asegura que el anillo termine donde empieza (cierre GeoJSON). */
export function cerrarAnillo(anillo: Coordenada[]): Coordenada[] {
  if (anillo.length === 0) return anillo;
  const [x0, y0] = anillo[0];
  const [xn, yn] = anillo[anillo.length - 1];
  if (x0 === xn && y0 === yn) return anillo;
  return [...anillo, anillo[0]];
}

/** Distancia Haversine entre dos coordenadas [lng, lat], en metros. */
export function distanciaMetros(a: Coordenada, b: Coordenada): number {
  const rad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * rad;
  const dLng = (b[0] - a[0]) * rad;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * RADIO_TIERRA_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Distancia punto→segmento en METROS (proyección métrica local en lat0 —
 *  suficiente para distancias de campo, decenas/cientos de metros). */
export function distanciaPuntoSegmentoM(
  p: Coordenada,
  s0: Coordenada,
  s1: Coordenada,
  lat0 = p[1]
): { distM: number; punto: Coordenada } {
  const kx = 111320 * Math.cos((lat0 * Math.PI) / 180);
  const ky = 110540;
  const px = p[0] * kx;
  const py = p[1] * ky;
  const ax = s0[0] * kx;
  const ay = s0[1] * ky;
  const bx = s1[0] * kx;
  const by = s1[1] * ky;
  const dx = bx - ax;
  const dy = by - ay;
  const largo2 = dx * dx + dy * dy;
  let t = 0;
  if (largo2 > 0) {
    t = ((px - ax) * dx + (py - ay) * dy) / largo2;
    t = Math.max(0, Math.min(1, t));
  }
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return { distM: Math.hypot(px - cx, py - cy), punto: [cx / kx, cy / ky] };
}

/** Perímetro de una línea o del anillo exterior de un polígono, en metros. */
export function perimetroMetros(coords: Coordenada[]): number {
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    total += distanciaMetros(coords[i - 1], coords[i]);
  }
  return total;
}

/**
 * Centroide AREAL del anillo de un polígono (shoelace ponderado por área).
 * El promedio de vértices (`centroide`) se sesga hacia el lado con más
 * vértices: un cuadro dibujado siguiendo un río o un camino acumula puntos
 * en un borde y su "centro" queda pegado a ese lado. null si el área es ~0.
 */
export function centroideAreaPoligono(anillo: Coordenada[]): Coordenada | null {
  if (anillo.length < 3) return null;
  const c = cerrarAnillo(anillo);
  let a2 = 0; // área firmada × 2
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < c.length - 1; i++) {
    const [x1, y1] = c[i];
    const [x2, y2] = c[i + 1];
    const f = x1 * y2 - x2 * y1;
    a2 += f;
    cx += (x1 + x2) * f;
    cy += (y1 + y2) * f;
  }
  if (Math.abs(a2) < 1e-12) return null;
  return [cx / (3 * a2), cy / (3 * a2)];
}

/**
 * Centroide aproximado de una geometría (promedio de vértices / punto).
 * Suficiente para centrar popups y labels; no es el centroide exacto.
 */
export function centroide(geo: GeometriaGeoJSON): Coordenada | null {
  if (!geo?.coordinates) return null;
  if (geo.type === 'Point') return geo.coordinates;
  const coords = geo.type === 'Polygon' ? geo.coordinates[0] || [] : geo.coordinates;
  if (coords.length === 0) return null;
  let sumLng = 0;
  let sumLat = 0;
  // En polígonos cerrados el último punto repite al primero: se excluye.
  const fin = coords.length > 1 && mismoPunto(coords[0], coords[coords.length - 1]) ? coords.length - 1 : coords.length;
  for (let i = 0; i < fin; i++) {
    sumLng += coords[i][0];
    sumLat += coords[i][1];
  }
  return [sumLng / fin, sumLat / fin];
}

function mismoPunto(a: Coordenada, b: Coordenada): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

/**
 * Validación básica de una geometría para persistir:
 * - Point: 2 coordenadas finitas.
 * - LineString: >= 2 vértices.
 * - Polygon: >= 3 vértices distintos (el cierre lo agrega la UI al guardar).
 */
export function esGeometriaValida(geo: unknown): geo is GeometriaGeoJSON {
  if (!geo || typeof geo !== 'object') return false;
  const g = geo as GeometriaGeoJSON;
  const finitos = (c: Coordenada) =>
    Number.isFinite(c[0]) && Number.isFinite(c[1]);
  switch (g.type) {
    case 'Point':
      return Array.isArray(g.coordinates) && finitos(g.coordinates);
    case 'LineString':
      return Array.isArray(g.coordinates) && g.coordinates.length >= 2 && g.coordinates.every(finitos);
    case 'Polygon':
      return (
        Array.isArray(g.coordinates) &&
        Array.isArray(g.coordinates[0]) &&
        g.coordinates[0].length >= 3 &&
        (g.coordinates[0] as Coordenada[]).every(finitos)
      );
    default:
      return false;
  }
}

/** Redondea a 6 decimales (~0,1 m) para no arrastrar ruido de GPS. */
export function redondearCoordenada(c: Coordenada): Coordenada {
  return [Math.round(c[0] * 1e6) / 1e6, Math.round(c[1] * 1e6) / 1e6];
}

/**
 * Normaliza una geometría al estándar GeoJSON RFC 7946 — compatibilidad con
 * el SDK de Google (verificado contra google.maps.Data con la key del
 * proyecto, 2026-08-31):
 *   1. Anillos de polígono CERRADOS (primer == último punto). Google
 *      RECHAZA anillos abiertos ("first and last positions are not
 *      equivalent") — mobile dibujaba y guardaba anillos abiertos.
 *   2. Anillo exterior antihorario (CCW, regla de la mano derecha).
 *      Google tolera ambos sentidos, pero la spec lo pide y los exports
 *      estrictos (KML) también.
 *   3. Coordenadas a 6 decimales (~0,1 m).
 *
 * Aplicada en el repositorio (escrituras y lecturas), cubre web, mobile y
 * lo que ya quedó guardado sin normalizar.
 */
export function normalizarGeometria<T extends GeometriaGeoJSON>(geo: T): T {
  switch (geo.type) {
    case 'Point':
      return { ...geo, coordinates: redondearCoordenada(geo.coordinates) };
    case 'LineString':
      return { ...geo, coordinates: geo.coordinates.map(redondearCoordenada) };
    case 'Polygon':
      return {
        ...geo,
        coordinates: geo.coordinates.map((anillo, i) => {
          const redondo = anillo.map(redondearCoordenada);
          const cerrado = cerrarAnillo(redondo);
          // Anillo exterior (0): CCW. Interiores (huecos): CW — convención RFC.
          const esCCW = areaFirmada(cerrado) >= 0;
          return (i === 0 ? esCCW : !esCCW) ? cerrado : [...cerrado].reverse();
        })
      };
    default:
      return geo;
  }
}

/** Área firmada del anillo (shoelace) en [lng, lat]: > 0 = CCW. */
function areaFirmada(anillo: Coordenada[]): number {
  let suma = 0;
  const n = anillo.length;
  for (let i = 0; i < n - 1; i++) {
    suma += anillo[i][0] * anillo[i + 1][1] - anillo[i + 1][0] * anillo[i][1];
  }
  return suma;
}

/**
 * Envuelve una geometría en Feature GeoJSON — google.maps.Data SOLO acepta
 * Feature o FeatureCollection (rechaza geometrías puras con "not a Feature
 * or FeatureCollection"). `properties` viaja como metadatos del feature.
 */
export function aFeatureGeoJSON(
  geometria: GeometriaGeoJSON,
  properties: Record<string, unknown> = {}
): { type: 'Feature'; geometry: GeometriaGeoJSON; properties: Record<string, unknown> } {
  return { type: 'Feature', geometry: geometria, properties };
}

/**
 * Stats de elevación para una geometría: el llamado lo hace el
 * elevationService con los vértices; acá solo agregamos.
 */
export function resumenElevaciones(elevaciones: (number | null)[]): {
  min: number | null;
  max: number | null;
  promedio: number | null;
  desnivel: number | null;
} {
  const vals = elevaciones.filter((e): e is number => e !== null && Number.isFinite(e));
  if (vals.length === 0) return { min: null, max: null, promedio: null, desnivel: null };
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const promedio = vals.reduce((a, b) => a + b, 0) / vals.length;
  return { min, max, promedio, desnivel: Math.round((max - min) * 10) / 10 };
}

